import * as Ably from 'ably';
import { CLIRNET_API } from '../api/endpoints.js';
import { CHANNELS, ADMIN_CHANNELS, SESSION_DETAILS_HISTORY, buildChannelName } from '../channel/channels.js';
import { createAblyAuthCallback } from './ablyAuth.js';

// Keep Ably's data to this tab: after every connect it saves its preferred transport
// ("ably-transport-preference") in storage that outlives the tab. Marked unavailable, that
// stays in memory instead; its sessionStorage use (connection recovery) is unchanged.
const ablyStorage = Ably.Realtime.Platform?.WebStorage;
if (ablyStorage) ablyStorage.localSupported = false;

const REALTIME_ENABLED = true;

// Manual testing switch: set to true to never open the real Ably connection and report
// it as permanently lost, so the admin's "Technical issue detected" modal can be
// checked. Keep it false in commits - the ably tests fail while it is on.
const SIMULATE_ABLY_DOWN = false;

const CONNECT_TIMEOUT_MS = 5000;

// REST API version (X-Ably-Version) for client.request() - ably-js v2 takes it as an argument.
const ABLY_REST_API_VERSION = 3;

// Recovery from Ably's terminal 'failed' state. Ably retries 'disconnected' and
// 'suspended' on its own, but never 'failed' - so the provider does, a bounded number of
// times: exponential backoff (waits of ~2s, 4s, 8s, 16s, 32s) with jitter, so clients
// dropped by the same outage don't reconnect in lockstep, and after
// RECONNECT_MAX_ATTEMPTS failed tries it stops for good - the "technical issue" notice's
// Retry (a page reload) takes over. Every successful connection resets the count. Coming
// back online / the tab becoming visible cuts the current wait short (it counts as a try).
// Each attempt re-runs the authCallback (ablyAuth.js) with the current token, so it also
// recovers from an expired one, not just a network blip.
export const RECONNECT_BASE_MS = 2000;
export const RECONNECT_MAX_MS = 60000;
export const RECONNECT_MAX_ATTEMPTS = 5;

// Delay before reconnect attempt `attempt` (0-based): "equal jitter" - half of the
// exponential step is fixed, half random - so it is never 0 and never synchronized.
export function reconnectDelay(attempt, random = Math.random) {
  const step = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
  return Math.round(step / 2 + random() * (step / 2));
}

function getAblyTokenEndpoint() {
  const envUrl = import.meta.env?.ABLY_LOGIN_URL;
  if (envUrl) return envUrl;
  const base = String(CLIRNET_API.BASE_URL || '').replace(/\/$/, '');
  const login = String(CLIRNET_API.ABLY_LOGIN || 'ably/login').replace(/^\//, '');
  return `${base}/${login}`;
}

function buildSessionChannelName(sessionId, channel) {
  return buildChannelName(sessionId, channel);
}

function hasRealtimeCredentials() {
  return REALTIME_ENABLED;
}

let suppressionInstalled = false;
function installRealtimeErrorSuppression() {
  if (suppressionInstalled || typeof window === 'undefined') return;
  suppressionInstalled = true;
  window.addEventListener('unhandledrejection', (event) => {
    const r = event.reason;
    if (r?.constructor?.name === '_ErrorInfo' && r.message === 'Connection closed') event.preventDefault();
  });
}
installRealtimeErrorSuppression();

function toObject(raw) {
  let data = raw;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch { return null; }
  }
  return data && typeof data === 'object' ? data : null;
}

// The moderator never enters presence: the Ably token has no 'presence' capability, so an
// enter only fails (and shows as a connection.error on the dashboard).
function createAblyProvider({ clientId, sessionId }) {
  let channels = {};
  let client = null;
  let isConnected = false;
  // Ably itself gave up on the connection: 'failed' (terminal) or 'suspended' (down for
  // longer than Ably's ~2 min connection-state TTL). Short 'disconnected' blips do not
  // count, and the flag is sticky through the retry states ('connecting',
  // 'disconnected') - only a real 'connected' clears it.
  let permanentlyDisconnected = false;
  const listeners = {};
  const connectionHandlers = [];
  const permanentDisconnectHandlers = [];
  const presenceHandlers = [];

  // ---- reconnect after 'failed' (see RECONNECT_BASE_MS) ----
  let reconnectTimer = null;
  let reconnectAttempts = 0; // tries made in this outage - reset by a successful connection
  let removeWakeListeners = null;

  function clearReconnectTimer() {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  // One try now, on the same client - only while Ably is really in 'failed' (it is already
  // working on any other state) and tries are left. A 'failed' answer schedules the next,
  // longer wait.
  function reconnectNow() {
    clearReconnectTimer();
    if (!client || client.connection.state !== 'failed') return;
    if (reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) return;
    reconnectAttempts += 1;
    try {
      client.connect();
    } catch (err) {
      console.warn('[ably] reconnect attempt failed:', err?.message || err);
      scheduleReconnect();
    }
  }

  function scheduleReconnect() {
    if (reconnectTimer || !client) return;
    if (reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.warn(`[ably] gave up reconnecting after ${RECONNECT_MAX_ATTEMPTS} attempts - reload the page to try again`);
      return;
    }
    const delay = reconnectDelay(reconnectAttempts);
    console.warn(
      `[ably] connection failed - reconnecting in ${Math.round(delay / 1000)}s (attempt ${reconnectAttempts + 1} of ${RECONNECT_MAX_ATTEMPTS})`,
    );
    reconnectTimer = setTimeout(reconnectNow, delay);
  }

  // Back online / tab visible again: no point waiting out the current backoff. It uses up
  // one of the tries like any other, so wake events can never retry without end.
  function installWakeListeners() {
    if (removeWakeListeners || typeof window === 'undefined') return;
    const wake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (client?.connection.state !== 'failed') return;
      reconnectNow();
    };
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
    removeWakeListeners = () => {
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', wake);
      removeWakeListeners = null;
    };
  }

  let historyEpoch = 0;
  const historyCache = new Map(); 

  function bumpHistoryEpoch() {
    historyEpoch += 1;
    historyCache.clear();
  }

  // Channel reads wait for attach, which can hang while the connection is down.
  const HISTORY_TIMEOUT_MS = 10000;
  const PRESENCE_TIMEOUT_MS = 5000;
  function withTimeout(promise, ms, fallback) {
    let timer = null;
    return Promise.race([
      Promise.resolve(promise),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(fallback), ms);
      }),
    ])
      .catch(() => fallback)
      .finally(() => clearTimeout(timer));
  }

  async function fetchHistoryPages(ch, limit, maxPages) {
    const items = [];
    if (ch.state !== 'attached') await ch.attach();
    let page = await ch.history({ limit });
    let count = 0;
    while (page && count < maxPages) {
      for (const m of page.items) items.push({ id: m.id, timestamp: m.timestamp, name: m.name, data: m.data });
      count += 1;
      page = page.hasNext() ? await page.next() : null;
    }
    return items;
  }

  function getRawHistory(channel, { limit = 100, maxPages = 5 } = {}) {
    const ch = channels[channel];
    if (!ch) return Promise.resolve([]);
    const need = limit * maxPages;
    const cached = historyCache.get(channel);
    if (cached && cached.epoch === historyEpoch && cached.need >= need) return cached.promise;
    const epoch = historyEpoch;
    let timer = null;
    // ch.attach()/history() can hang on a dead socket, and THIS promise is cached: a
    // hang would poison the channel forever and make every later read burn its own
    // timeout. Cap it, and drop the cache entry on failure so callers can retry.
    const promise = Promise.race([
      fetchHistoryPages(ch, limit, maxPages),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(`history timed out for ${channel}`)), HISTORY_TIMEOUT_MS);
      }),
    ])
      .finally(() => clearTimeout(timer))
      .catch((err) => {
        if (historyCache.get(channel)?.promise === promise) historyCache.delete(channel);
        console.warn(`[ably][history] ${channel} failed:`, err?.message || err);
        throw err;
      });
    historyCache.set(channel, { epoch, need, promise });
    return promise;
  }

  const emit = (channel, data, isHistory = false, meta = {}) => {
    for (const fn of listeners[channel] || []) {
      try { fn(data, isHistory, meta); } catch (e) { console.warn(`[ably] handler failed for ${channel}`, e); }
    }
  };

  const notifyConnection = (connected) => {
    isConnected = connected;
    for (const fn of connectionHandlers) {
      try { fn(connected); } catch { /* one bad listener must not break the rest */ }
    }
  };

  const setPermanentlyDisconnected = (down) => {
    if (permanentlyDisconnected === down) return;
    permanentlyDisconnected = down;
    if (down) console.warn('[ably] connection is permanently disconnected - still retrying in the background');
    for (const fn of permanentDisconnectHandlers) {
      try { fn(down); } catch { /* one bad listener must not break the rest */ }
    }
  };

  function routeIncomingMessages(ch, channel) {
    ch.subscribe((msg) => {
      const data = toObject(msg.data);
      if (data) {
        historyCache.delete(channel);
        emit(channel, data, false, { id: msg.id, timestamp: msg.timestamp });
      }
    });
    ch.on('failed', (s) => console.warn(`[ably] channel "${channel}" FAILED:`, s?.reason?.message || 'unknown'));
  }

  async function replayFromHistory(channel) {
    try {
      // Session details must reach back to the last full snapshot; other channels
      // only need their newest page.
      const options = channel === CHANNELS.SESSION_DETAILS ? SESSION_DETAILS_HISTORY : { limit: 100, maxPages: 1 };
      const items = await getRawHistory(channel, options);
      if (!items.length) return;
      [...items].reverse().forEach((item) => {
        const data = toObject(item.data);
        if (data) emit(channel, data, true, { id: item.id, timestamp: item.timestamp });
      });
    } catch (err) {
      console.warn(`[ably][history] ${channel} failed:`, err?.message || err);
    }
  }

  async function refreshPresence() {
    try {
      const members = await withTimeout(
        channels[CHANNELS.SESSION_DETAILS]?.presence.get(),
        PRESENCE_TIMEOUT_MS,
        null,
      );
      if (!members) return;
      const list = members.map((m) => ({ ...m.data, connectionId: m.connectionId }));
      for (const fn of presenceHandlers) {
        try { fn(list); } catch { /* one bad listener must not break the rest */ }
      }
    } catch (err) {
      console.warn('[ably][presence] refresh failed:', err?.message || err);
    }
  }

  return {
    get isConnected() { return isConnected; },
    get isPermanentlyDisconnected() { return permanentlyDisconnected; },
    onConnectionChange(handler) { connectionHandlers.push(handler); },
    onPermanentDisconnectChange(handler) { permanentDisconnectHandlers.push(handler); },
    subscribe(channel, handler) {
      const list = (listeners[channel] ||= []);
      if (!list.includes(handler)) list.push(handler);
    },
    unsubscribe(channel, handler) {
      listeners[channel] = (listeners[channel] || []).filter((fn) => fn !== handler);
    },
    async getPresenceMembers() {
      try {
        const members = await channels[CHANNELS.SESSION_DETAILS]?.presence.get();
        return (members || []).map((m) => ({ ...m.data, connectionId: m.connectionId }));
      } catch { return []; }
    },
    onPresenceChange(handler) { presenceHandlers.push(handler); },
    offPresenceChange(handler) {
      const i = presenceHandlers.indexOf(handler);
      if (i !== -1) presenceHandlers.splice(i, 1);
    },
    async getChannelHistory(channel, { limit = 100, maxPages = 5 } = {}) {
      if (!channels[channel]) return [];
      try {
        const raw = await getRawHistory(channel, { limit, maxPages });
        return raw.slice(0, limit * maxPages).map((m) => toObject(m.data)).filter((d) => d !== null);
      } catch { return []; }
    },
    async getChannelHistoryWithMeta(channel, { limit = 100, maxPages = 5, strict = false } = {}) {
      if (!channels[channel]) {
        // Callers treat [] as "channel has nothing" - fine for the app, but a
        // diagnostic (devconfig) needs to tell "no messages" apart from
        // "channel missing / attach denied".
        if (strict) throw new Error(`No channel "${channel}" (not attached)`);
        return [];
      }
      try {
        const raw = await getRawHistory(channel, { limit, maxPages });
        const items = [];
        for (const m of raw.slice(0, limit * maxPages)) {
          const data = toObject(m.data);
          if (data !== null) items.push({ data, id: m.id, timestamp: m.timestamp, name: m.name });
        }
        return items;
      } catch (err) {
        if (strict) throw err;
        return [];
      }
    },
    // Who is on a session channel right now - Ably's channel status (REST GET
    // /channels/{name}): the connections / publishers / subscribers attached at this
    // moment. Needs the token's 'channel-metadata' capability on that channel.
    async getChannelOccupancy(channel) {
      if (!client) throw new Error('not connected');
      const name = encodeURIComponent(buildSessionChannelName(sessionId, channel));
      const res = await client.request('get', `/channels/${name}`, ABLY_REST_API_VERSION);
      if (!res?.success) throw new Error(res?.errorMessage || `channel status failed (${res?.statusCode})`);
      // The channel status object is the response's only item.
      const metrics = res.items?.[0]?.status?.occupancy?.metrics || {};
      return {
        connections: metrics.connections ?? 0,
        publishers: metrics.publishers ?? 0,
        subscribers: metrics.subscribers ?? 0,
      };
    },
    connect() {
      if (SIMULATE_ABLY_DOWN) {
        console.warn('[ably] SIMULATE_ABLY_DOWN is on - not connecting, reporting the connection as permanently lost');
        notifyConnection(false);
        setPermanentlyDisconnected(true);
        return Promise.resolve();
      }
      client = new Ably.Realtime({
        authCallback: createAblyAuthCallback({ endpoint: getAblyTokenEndpoint(), clientId, sessionId }),
        clientId,
      });
      installWakeListeners();

      let subscribed = false;
      return new Promise((resolve, reject) => {
        let timeout = setTimeout(() => console.warn(`[ably] not connected after ${CONNECT_TIMEOUT_MS}ms — still trying…`), CONNECT_TIMEOUT_MS);

        const clearTimer = () => { clearTimeout(timeout); };

        const fail = (err) => {
          clearTimer();
          notifyConnection(false);
          console.warn('[ably] connection failed:', err?.message || err);
          scheduleReconnect();
          reject(err); // only affects the very first connect() call - later retries just log + notify
        };

        const disconnect = () => {
          clearTimer();
          notifyConnection(false);
        };

        // Ably's own "this is not a blip" states. Recovery keeps running (Ably retries
        // 'suspended' itself, scheduleReconnect retries 'failed' up to 5 times).
        const lost = () => setPermanentlyDisconnected(true);

        client.connection.on('connected', () => {
          clearTimer();
          clearReconnectTimer(); // a reconnect just succeeded - nothing left to retry
          reconnectAttempts = 0; // healthy again - a new outage gets its full set of tries
          setPermanentlyDisconnected(false);
          bumpHistoryEpoch();
          if (subscribed) {
            notifyConnection(true);
            return;
          }
          channels = Object.fromEntries(
            [...Object.values(CHANNELS), ...Object.values(ADMIN_CHANNELS)].map((purpose) => {
              const ch = client.channels.get(buildSessionChannelName(sessionId, purpose), { params: { rewind: '1' } });
              routeIncomingMessages(ch, purpose);
              return [purpose, ch];
            }),
          );
          channels[CHANNELS.SESSION_DETAILS].presence.subscribe(['enter', 'leave', 'update'], refreshPresence);
          subscribed = true;
          notifyConnection(true);
          setTimeout(() => Object.keys(channels).forEach((purpose) => replayFromHistory(purpose)), 250);
          resolve();
        });

        client.connection.on('failed', fail);
        client.connection.on('failed', lost);
        client.connection.on('suspended', disconnect);
        client.connection.on('suspended', lost);
        client.connection.on('disconnected', disconnect);
        client.connection.on('closed', disconnect);
      });
    },
    disconnect() {
      // Stop trying to heal a connection we're intentionally tearing down.
      clearReconnectTimer();
      reconnectAttempts = 0;
      removeWakeListeners?.();
      permanentlyDisconnected = false; // an intentional teardown is not an outage
      if (!client) return;
      try { channels[CHANNELS.SESSION_DETAILS]?.presence.unsubscribe(); } catch { /* best-effort unsubscribe */ }
      for (const ch of Object.values(channels)) {
        try { ch.unsubscribe(); } catch { /* best-effort unsubscribe */ }
        try { ch.off(); } catch { /* best-effort off */ }
      }
      try { client.connection.off(); } catch { /* best-effort off */ }
      const closing = client.close();
      if (closing?.catch) closing.catch(() => {});
      client = null;
      channels = {};
      isConnected = false;
    },
    publish(channel, data, name) {
      const ch = channels[channel];
      if (!ch) throw new Error(`No channel for "${channel}"`);
      return ch.publish(name ? { name, data } : { data });
    },
  };
}

let provider = null;
let connectionHandlers = [];
let permanentDisconnectHandlers = [];
let presenceHandlers = [];

function notifyPermanentDisconnect(down) {
  for (const fn of permanentDisconnectHandlers) {
    try { fn(down); } catch { /* one bad listener must not break the rest */ }
  }
}
let pendingSubscriptions = [];
let pendingPublishes = [];

function flushPending() {
  for (const { channel, handler } of pendingSubscriptions) provider.subscribe(channel, handler);
  pendingSubscriptions = [];
  for (const { channel, data, name, resolve, reject } of pendingPublishes) {
    try {
      provider.publish(channel, data, name).then(resolve, reject);
    } catch (e) {
      // publish can throw synchronously (no channel yet) - settle the queued promise
      // instead of aborting the whole flush.
      reject(e);
    }
  }
  pendingPublishes = [];
}

function rejectPending(message) {
  for (const { reject } of pendingPublishes) reject(new Error(message));
  pendingPublishes = [];
}

// History/presence reads wait for the channel to attach, which can hang while the
// connection is down. Cap them so callers (which all treat [] as "no data") are never
// blocked on a dead socket - the same hang that can freeze an awaited publish.
const READ_TIMEOUT_MS = 10000;
function withReadTimeout(promise, ms = READ_TIMEOUT_MS, fallback = []) {
  let timer = null;
  const safe = Promise.resolve(promise).catch(() => fallback);
  return Promise.race([
    safe,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(fallback), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

// Like withReadTimeout, but a timeout rejects - for callers that report the failure.
function rejectAfter(promise, message, ms = READ_TIMEOUT_MS) {
  let timer = null;
  const timedOut = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timedOut]).finally(() => clearTimeout(timer));
}

export const pubSub = {
  get isConnected() { return provider ? provider.isConnected : false; },

  // True while Ably has given up on the connection ('failed' / 'suspended'), until it is
  // 'connected' again. Drives the admin's "technical issue" modal.
  get isPermanentlyDisconnected() { return provider ? provider.isPermanentlyDisconnected : false; },

  init({ clientId, sessionId, userInfo }) {
    if (!hasRealtimeCredentials()) return;
    // A fresh provider starts healthy - clear a lost state left by the one it replaces.
    const wasPermanentlyDisconnected = provider ? provider.isPermanentlyDisconnected : false;
    if (provider) provider.disconnect();
    const p = createAblyProvider({ clientId, sessionId, userInfo });
    provider = p;
    if (wasPermanentlyDisconnected) notifyPermanentDisconnect(false);
    for (const fn of presenceHandlers) p.onPresenceChange(fn);
    p.onPermanentDisconnectChange((down) => {
      if (provider === p) notifyPermanentDisconnect(down);
    });
    p.onConnectionChange((connected) => {
      if (provider !== p) return;
      for (const fn of connectionHandlers) {
        try { fn(connected); } catch { /* one bad listener must not break the rest */ }
      }
      if (connected) {
        try {
          flushPending();
        } catch (e) {
          console.warn('[pubSub] flushing queued publishes failed:', e?.message || e);
        }
      }
    });
    p.connect().catch((err) => {
      // Don't discard the provider or reject queued work here - it keeps
      // retrying on its own (see createAblyProvider's scheduleReconnect),
      // and onConnectionChange(true) above will flush anything queued the
      // moment it actually connects. Tearing the provider down here would
      // make that self-healing invisible to the rest of the app.
      console.warn('[pubSub] initial connect failed, will keep retrying in the background:', err?.message || err);
    });
  },

  disconnect() {
    rejectPending('Ably disconnected');
    if (provider) provider.disconnect();
    provider = null;
    connectionHandlers = [];
    permanentDisconnectHandlers = [];
    presenceHandlers = [];
    pendingSubscriptions = [];
    pendingPublishes = [];
  },

  onConnectionChange(handler) { connectionHandlers.push(handler); },
  offConnectionChange(handler) { connectionHandlers = connectionHandlers.filter((fn) => fn !== handler); },

  onPermanentDisconnectChange(handler) { permanentDisconnectHandlers.push(handler); },
  offPermanentDisconnectChange(handler) {
    permanentDisconnectHandlers = permanentDisconnectHandlers.filter((fn) => fn !== handler);
  },

  subscribe(channel, handler) {
    if (provider) provider.subscribe(channel, handler);
    else pendingSubscriptions.push({ channel, handler });
  },

  unsubscribe(channel, handler) {
    if (provider) provider.unsubscribe(channel, handler);
    else pendingSubscriptions = pendingSubscriptions.filter((s) => s.channel !== channel || s.handler !== handler);
  },

  publish(channel, data, name) {
    if (provider && provider.isConnected) {
      const result = provider.publish(channel, data, name);
      Promise.resolve(result).catch((err) => console.warn(`[pubSub] publish(${channel}) failed:`, err?.message || err));
      return result;
    }
    return new Promise((resolve, reject) => {
      pendingPublishes.push({ channel, data, name, resolve, reject });
    });
  },

  async getPresenceMembers() {
    return withReadTimeout(
      provider ? provider.getPresenceMembers() : Promise.resolve([]),
      5000,
      [],
    );
  },

  async getChannelHistory(channel, options) {
    return withReadTimeout(provider ? provider.getChannelHistory(channel, options) : Promise.resolve([]));
  },

  async getChannelHistoryWithMeta(channel, options) {
    // `strict: true` propagates the failure (and the timeout) instead of
    // resolving [] - only used where the caller reports the outcome.
    if (options?.strict) {
      if (!provider) throw new Error("not connected");
      return rejectAfter(provider.getChannelHistoryWithMeta(channel, options), `history timed out for ${channel}`);
    }
    return withReadTimeout(provider ? provider.getChannelHistoryWithMeta(channel, options) : Promise.resolve([]));
  },

  // A session channel's occupancy (see the provider's getChannelOccupancy). Throws when it
  // can't be read: no Ably client yet, the request refused, or no answer in time.
  async getChannelOccupancy(channel) {
    if (!provider) throw new Error('not connected');
    return rejectAfter(provider.getChannelOccupancy(channel), `channel status timed out for ${channel}`);
  },

  onPresenceChange(handler) {
    presenceHandlers.push(handler);
    if (provider) provider.onPresenceChange(handler);
  },

  offPresenceChange(handler) {
    presenceHandlers = presenceHandlers.filter((fn) => fn !== handler);
    if (provider) provider.offPresenceChange(handler);
  },
};

if (import.meta.env.DEV && typeof window !== 'undefined') window.pubSub = pubSub;
